/**
 * Size modal: Fit Finder + size chart (STCHD-11). Markup in snippets/size-chart-modal.liquid,
 * data in its JSON block, modal mechanics from the theme's popup-modal (assets/magnet.js):
 * close button and overlay click are its own, Escape closes every popup globally.
 *
 * This file adds: opening from the "Finde deine Größe" trigger (STCHD-10) with focus return,
 * tabs, input validation, the recommendation, the length toggles of the chart, the apply
 * button that sets the picker, session storage of the measurements, and the cross-product
 * preselect of the recommended size + length on page load.
 */

(function () {
  // Typo guards for the inputs. Not the ranges that decide the recommendation — those come from
  // the chart rows.
  const BOUNDS = {
    waist: [40, 150],
    inseam: [50, 110],
    height: [120, 210]
  };
  const STORAGE_KEY = 'stchd:fitfinder';
  // Tolerance when the chart has no series to derive one from (one row, one length).
  const FALLBACK_TOLERANCE = 2.5;
  const EPSILON = 1e-9;

  function readStorage() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      // Private window or blocked site data: the finder simply starts empty.
      return null;
    }
  }

  function writeStorage(data) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      /* same as above */
    }
  }

  /** "72", "72,5" and "72.5" are numbers; "abc" and "" are not. */
  function parseMeasure(raw) {
    const text = String(raw || '').trim().replace(',', '.');
    if (!text || !/^\d+(\.\d+)?$/.test(text)) return NaN;
    return Number(text);
  }

  function isNumber(value) {
    return typeof value === 'number' && !Number.isNaN(value);
  }

  function hasRange(min, max) {
    return isNumber(min) && isNumber(max) && max >= min;
  }

  /** CSS.escape with a fallback for engines without it (quotes and backslashes only matter here). */
  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function median(values) {
    if (!values.length) return null;
    const sorted = values.slice().sort(function (a, b) { return a - b; });
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  }

  /**
   * Tolerances per product, read off the chart. A range (waist, height) already covers its own
   * width, so its tolerance is half the step, the median range width. An inseam value covers
   * nothing by itself, so its tolerance is the full step, the median distance between
   * neighbouring values: it reaches the next value on either side. A series of one (one row,
   * one length) has no step; then 2.5 cm.
   */
  function tolerances(data) {
    const rows = data.rows || [];
    const lengths = data.lengths || [];
    const waistWidths = [];
    const heightWidths = [];
    const inseamSteps = [];

    rows.forEach(function (row) {
      if (hasRange(row.waistMin, row.waistMax)) waistWidths.push(row.waistMax - row.waistMin);
      let previous = null;
      lengths.forEach(function (entry) {
        const range = (row.height || {})[entry.key] || [];
        if (hasRange(range[0], range[1])) heightWidths.push(range[1] - range[0]);
        const value = (row.inseam || {})[entry.key];
        if (isNumber(value)) {
          if (previous !== null) inseamSteps.push(Math.abs(value - previous));
          previous = value;
        }
      });
    });

    const full = function (step) {
      return isNumber(step) && step > 0 ? step : FALLBACK_TOLERANCE;
    };
    const half = function (step) {
      return isNumber(step) && step > 0 ? step / 2 : FALLBACK_TOLERANCE;
    };
    return {
      waist: rows.length > 1 ? half(median(waistWidths)) : FALLBACK_TOLERANCE,
      inseam: lengths.length > 1 ? full(median(inseamSteps)) : FALLBACK_TOLERANCE,
      height: lengths.length > 1 ? half(median(heightWidths)) : FALLBACK_TOLERANCE
    };
  }

  /**
   * Deviation of a value from a range in two normalised parts: inside (0 at the middle, 1 at the
   * edge) and outside (0 at the edge, 1 at the tolerance). Inside the range outside is 0, so the
   * weights below are weights and do not depend on the width of the range.
   */
  function rangeDeviation(value, min, max, tolerance) {
    const middle = (min + max) / 2;
    const half = (max - min) / 2;
    const distance = Math.abs(value - middle);
    return {
      inside: half > 0 ? Math.min(distance, half) / half : 0,
      outside: Math.max(0, distance - half) / tolerance
    };
  }

  function rangeScore(deviation) {
    return 0.25 * deviation.inside * deviation.inside + 8 * deviation.outside * deviation.outside;
  }

  function within(value, min, max, tolerance) {
    return value >= min - tolerance && value <= max + tolerance;
  }

  /** Smaller score wins; a tie goes to the larger size, then to the longer length. */
  function better(candidate, winner) {
    if (!winner) return true;
    if (candidate.score < winner.score - EPSILON) return true;
    if (candidate.score > winner.score + EPSILON) return false;
    if (candidate.sizeIndex !== winner.sizeIndex) return candidate.sizeIndex > winner.sizeIndex;
    return (candidate.lengthIndex || 0) > (winner.lengthIndex || 0);
  }

  /**
   * The recommendation, in two steps: collect, then select.
   *
   * Collect: every row whose waist range contains the value or lies within the tolerance is a
   * size candidate — each row on its own, the first and last without a special role. For each
   * size candidate, every length whose inseam is within the tolerance (height range likewise,
   * when no inseam is given) is a length candidate. Nothing left: no recommendation.
   *
   * Select: score = 0.25·d_inside² + 8·d_outside² + d_length², the sum of the weighted squared
   * deviations, each normalised to its own scale (half width, tolerance). Smallest score wins;
   * ties go to the larger size and the longer length.
   *
   * rows in display order (ascending sizes), lengths in option order (ascending).
   */
  function recommend(data, waist, inseam, height) {
    const rows = data.rows || [];
    const lengths = data.lengths || [];
    const tolerance = tolerances(data);

    const sizes = [];
    rows.forEach(function (row, index) {
      if (!hasRange(row.waistMin, row.waistMax)) return;
      if (!within(waist, row.waistMin, row.waistMax, tolerance.waist)) return;
      const deviation = rangeDeviation(waist, row.waistMin, row.waistMax, tolerance.waist);
      sizes.push({ row: row, sizeIndex: index, score: rangeScore(deviation), inRange: deviation.outside === 0 });
    });

    function lengthScore(row, entry, source) {
      if (source === 'inseam') {
        const value = (row.inseam || {})[entry.key];
        if (!isNumber(value) || !within(inseam, value, value, tolerance.inseam)) return null;
        const deviation = Math.abs(inseam - value) / tolerance.inseam;
        return deviation * deviation;
      }
      const range = (row.height || {})[entry.key] || [];
      if (!hasRange(range[0], range[1]) || !within(height, range[0], range[1], tolerance.height)) return null;
      return rangeScore(rangeDeviation(height, range[0], range[1], tolerance.height));
    }

    function pairs(source) {
      const found = [];
      sizes.forEach(function (size) {
        lengths.forEach(function (entry, lengthIndex) {
          const score = lengthScore(size.row, entry, source);
          if (score === null) return;
          found.push({ size: size, sizeIndex: size.sizeIndex, length: entry, lengthIndex: lengthIndex, score: size.score + score, source: source });
        });
      });
      return found;
    }

    // The inseam is the more precise measure and decides when given; the height only steps in
    // when there is no inseam or no length is within its tolerance.
    let candidates = [];
    if (isNumber(inseam)) candidates = pairs('inseam');
    if (!candidates.length && isNumber(height)) candidates = pairs('height');

    let best = null;
    candidates.forEach(function (candidate) {
      if (better(candidate, best)) best = candidate;
    });

    // No pair: the size that fits the waist best stands alone, the length stays open.
    let bestSize = best ? best.size : null;
    if (!bestSize) {
      sizes.forEach(function (size) {
        if (better(size, bestSize)) bestSize = size;
      });
    }

    return {
      size: bestSize ? bestSize.row.size : null,
      sizeRow: bestSize ? bestSize.row : null,
      waistInRange: bestSize ? bestSize.inRange : false,
      length: best ? best.length.value : null,
      lengthSource: best ? best.source : null,
      score: best ? best.score : null,
      tolerances: tolerance
    };
  }

  class SizeChartModal extends HTMLElement {
    connectedCallback() {
      const dataNode = this.querySelector('[data-size-chart-data]');
      if (!dataNode) return;
      try {
        this.data = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.error('[size-chart] data block is not valid JSON', error);
        return;
      }

      this.popup = this.querySelector('[data-popup-modal]');
      this.root = this.querySelector('[data-size-chart-modal]');
      if (!this.popup || !this.root) return;

      this.checkLengthKeys();

      // The theme's popup carries no dialog semantics of its own (popup.liquid stays untouched).
      this.popup.setAttribute('role', 'dialog');
      this.popup.setAttribute('aria-modal', 'true');
      const title = this.root.querySelector('.size-chart-modal__title');
      if (title && title.id) this.popup.setAttribute('aria-labelledby', title.id);

      this.sectionId = this.dataset.section;
      this.popupAttribute = this.dataset.popup;
      this.strings = this.data.strings || {};
      this.inputs = {};
      this.root.querySelectorAll('[data-measure]').forEach((input) => {
        this.inputs[input.dataset.measure] = input;
        input.addEventListener('input', () => this.onInput(input));
      });
      this.result = this.root.querySelector('[data-result]');
      this.applyButton = this.root.querySelector('[data-apply]');
      if (this.applyButton) this.applyButton.addEventListener('click', () => this.apply());

      this.root.querySelectorAll('[data-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.showTab(tab.dataset.tab));
        tab.addEventListener('keydown', (event) => this.onTabKey(event, tab));
      });
      this.root.querySelectorAll('[data-length]').forEach((button) => {
        button.addEventListener('click', () => this.showLength(button.dataset.length));
      });

      // The trigger lives inside variants-set, which the theme rewrites on every variant change,
      // so the click is caught on the document and matched by section.
      this.onDocumentClick = (event) => {
        const trigger = event.target.closest('[data-size-guide-trigger]');
        if (!trigger) return;
        const section = trigger.closest('.shopify-section, section, [data-section]');
        if (section && section.id && section.id !== 'shopify-section-' + this.sectionId && !section.contains(this)) return;
        event.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onDocumentClick);

      // Closing happens in the theme (close button, overlay, Escape); focus goes back to the
      // trigger when the popup loses its show class.
      this.observer = new MutationObserver(() => {
        const shown = this.popup.classList.contains('show');
        if (this.wasShown && !shown) this.onClosed();
        this.wasShown = shown;
      });
      this.observer.observe(this.popup, { attributes: true, attributeFilter: ['class'] });

      // restore() recomputes the stored measurements against this product's chart; the picker is
      // then preselected from that fresh result, never from what another product's chart gave.
      this.restore();
      this.preselect();
      this.renderChart(this.activeLength());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.observer) this.observer.disconnect();
    }

    /**
     * The row fields are fixed (inseam_xshort … inseam_tall, height_*_…), the keys come from the
     * product's option values. Liquid writes all four keys into every row, so a key missing from
     * the row object altogether (undefined, not null) is an option value that maps to no field:
     * without this warning that length would silently stay empty in the table and never be
     * recommended. null only means the field is not filled in.
     */
    checkLengthKeys() {
      const row = (this.data.rows || [])[0];
      if (!row || !row.inseam) return;
      (this.data.lengths || []).forEach((entry) => {
        if (Object.prototype.hasOwnProperty.call(row.inseam, entry.key)) return;
        console.warn('[size-chart] length option value "' + entry.value + '" (key "' + entry.key + '") has no inseam_' + entry.key + ' / height_*_' + entry.key + ' field in size_chart_row: this length stays empty in the chart and is never recommended.');
      });
    }

    // -------------------------------------------------------------------------
    // open / close
    // -------------------------------------------------------------------------

    open(trigger) {
      this.trigger = trigger;
      this.showTab('finder');
      this.renderChart(this.activeLength());
      const popupHost = document.querySelector('popup-modal');
      if (popupHost && typeof popupHost.modalPopupOpen === 'function') {
        popupHost.modalPopupOpen(this.popupAttribute);
      } else {
        this.popup.style.display = 'flex';
        this.popup.classList.add('show');
        document.body.classList.add('overflow-hidden');
      }
      // after the theme's 400ms reveal, move focus into the dialog
      setTimeout(() => {
        const first = this.inputs.waist;
        if (first && this.popup.classList.contains('show')) first.focus();
      }, 450);
    }

    onClosed() {
      if (this.trigger && this.trigger.isConnected) {
        this.trigger.focus();
      } else {
        // the picker was re-rendered meanwhile: the trigger of the same section
        const trigger = document.querySelector('#shopify-section-' + this.sectionId + ' [data-size-guide-trigger]');
        if (trigger) trigger.focus();
      }
    }

    close() {
      const closer = this.popup.querySelector('[data-popup-close]');
      if (closer) closer.click();
    }

    // -------------------------------------------------------------------------
    // tabs
    // -------------------------------------------------------------------------

    showTab(name) {
      this.root.querySelectorAll('[data-tab]').forEach((tab) => {
        const active = tab.dataset.tab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.root.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.classList.toggle('hidden', panel.dataset.panel !== name);
      });
    }

    onTabKey(event, tab) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const tabs = Array.from(this.root.querySelectorAll('[data-tab]'));
      const index = tabs.indexOf(tab);
      const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      this.showTab(next.dataset.tab);
      next.focus();
    }

    // -------------------------------------------------------------------------
    // size chart tab
    // -------------------------------------------------------------------------

    /** The length selected in the picker right now, else the first one. The key comes from the data, not from the value. */
    activeLength() {
      const checked = this.pickerInput(this.data.lengthOption, null, true);
      const selected = checked ? this.data.lengths.find((entry) => entry.value === checked.value) : null;
      if (selected) return selected.key;
      return this.data.lengths.length ? this.data.lengths[0].key : null;
    }

    showLength(key) {
      this.renderChart(key);
    }

    renderChart(key) {
      this.root.querySelectorAll('[data-length]').forEach((button) => {
        const active = button.dataset.length === key;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-row-size]').forEach((tr) => {
        const row = this.data.rows.find((entry) => entry.size === tr.dataset.rowSize);
        const cell = tr.querySelector('[data-cell="inseam"]');
        if (!cell) return;
        const value = row && key ? row.inseam[key] : null;
        // no value for this length: the cell stays empty, no zero
        cell.textContent = isNumber(value) ? value + ' ' + (this.strings.unitCm || 'cm') : '';
      });
    }

    // -------------------------------------------------------------------------
    // fit finder
    // -------------------------------------------------------------------------

    onInput(input) {
      this.validate(input);
      this.update();
    }

    /** Returns the valid number, or NaN; shows / clears the field's own message. */
    validate(input) {
      const name = input.dataset.measure;
      const error = this.root.querySelector('[data-error-for="' + name + '"]');
      const raw = input.value.trim();
      let message = '';
      let value = NaN;

      if (raw !== '') {
        value = parseMeasure(raw);
        if (Number.isNaN(value)) {
          message = this.strings.errorNumber || '';
        } else {
          const [min, max] = BOUNDS[name];
          if (value < min || value > max) {
            message = (this.strings.errorRange || '').replace('__MIN__', min).replace('__MAX__', max);
            value = NaN;
          }
        }
      }

      if (error) {
        error.textContent = message;
        error.classList.toggle('hidden', !message);
      }
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      input.classList.toggle('is-invalid', !!message);
      return value;
    }

    values() {
      return {
        waist: this.validate(this.inputs.waist),
        inseam: this.inputs.inseam ? this.validate(this.inputs.inseam) : NaN,
        height: this.inputs.height ? this.validate(this.inputs.height) : NaN
      };
    }

    update() {
      const { waist, inseam, height } = this.values();
      const hasLength = isNumber(inseam) || isNumber(height);
      if (!isNumber(waist) || !hasLength) {
        this.recommendation = null;
        this.result.classList.add('hidden');
        this.applyButton.classList.add('hidden');
        this.remember({ waist: this.inputs.waist.value, inseam: this.inputs.inseam.value, height: this.inputs.height.value, size: null, length: null });
        return;
      }

      const rec = recommend(this.data, waist, inseam, height);
      this.recommendation = rec;
      this.render(rec, waist);
      this.remember({ waist: this.inputs.waist.value, inseam: this.inputs.inseam.value, height: this.inputs.height.value, size: rec.size, length: rec.length });
    }

    render(rec, waist) {
      const s = this.strings;
      this.root.querySelector('[data-result-size]').textContent = rec.size || s.unknown || '–';
      this.root.querySelector('[data-result-length]').textContent = rec.length || s.unknown || '–';

      const reasons = [];
      if (rec.sizeRow) {
        const reason = rec.waistInRange ? s.reasonWaist : s.reasonWaistNear;
        reasons.push((reason || '').replace('__WAIST__', waist).replace('__MIN__', rec.sizeRow.waistMin).replace('__MAX__', rec.sizeRow.waistMax));
        if (rec.length) {
          reasons.push(rec.lengthSource === 'inseam' ? s.reasonInseam || '' : s.reasonHeight || '');
        } else {
          reasons.push(s.noLength || '');
        }
      } else {
        // without a size there is no row to read a length off, so only the waist is explained
        reasons.push(s.noSize || '');
      }
      this.root.querySelector('[data-result-reason]').textContent = reasons.filter(Boolean).join(' ');

      const note = this.root.querySelector('[data-result-note]');
      const complete = !!(rec.size && rec.length);
      const available = complete && this.isAvailable(rec.size, rec.length);
      note.textContent = complete && !available ? s.unavailable || '' : '';
      note.classList.toggle('hidden', !(complete && !available));

      this.result.classList.remove('hidden');
      this.applyButton.classList.toggle('hidden', !complete);
    }

    /**
     * Whether size + length can be bought together with whatever else is selected right now
     * (the colour, typically). Without a selection for one of the other options, any variant
     * with that size + length counts.
     */
    isAvailable(size, length) {
      const names = this.data.optionNames || [];
      const wanted = {};
      names.forEach((name) => {
        if (name === this.data.sizeOption) wanted[name] = size;
        else if (name === this.data.lengthOption) wanted[name] = length;
        else {
          const checked = this.pickerInput(name, null, true);
          wanted[name] = checked ? checked.value : null;
        }
      });
      return (this.data.variants || []).some((variant) => {
        return variant.available === true && names.every((name, index) => {
          return wanted[name] === null || variant.options[index] === wanted[name];
        });
      });
    }

    remember(state) {
      writeStorage(state);
    }

    /**
     * Same visit, same finder: the fields come back and the recommendation is computed afresh
     * against this product's chart (and stored again, so the storage always holds the latest).
     */
    restore() {
      const stored = readStorage();
      if (!stored) return;
      ['waist', 'inseam', 'height'].forEach((name) => {
        if (this.inputs[name] && stored[name]) this.inputs[name].value = stored[name];
      });
      if (stored.waist || stored.inseam || stored.height) this.update();
    }

    // -------------------------------------------------------------------------
    // the picker
    // -------------------------------------------------------------------------

    /** The radio for an option value in this section's picker; checked=true asks for the selected one. */
    pickerInput(optionName, value, checked) {
      if (!optionName) return null;
      const section = document.getElementById('shopify-section-' + this.sectionId) || document;
      const sets = section.querySelectorAll('variants-set optionset[data-optionset="' + cssEscape(optionName) + '"]');
      for (const set of sets) {
        const input = checked ? set.querySelector('input.productOption:checked') : set.querySelector('input.productOption[value="' + cssEscape(value) + '"]');
        if (input) return input;
      }
      return null;
    }

    /** Checks an option value in the picker and lets the theme react as to a click. */
    select(optionName, value) {
      const input = this.pickerInput(optionName, value, false);
      if (!input || input.checked) return !!input;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    apply() {
      const rec = this.recommendation;
      if (!rec || !rec.size || !rec.length) return;
      this.select(this.data.sizeOption, rec.size);
      if (this.data.lengthOption) this.select(this.data.lengthOption, rec.length);
      this.close();
    }

    /** A variant in the URL (?variant=, from a newsletter or an ad) is a choice already made. */
    hasVariantInUrl() {
      try {
        return new URL(window.location.href).searchParams.has('variant');
      } catch (error) {
        return /[?&]variant=/.test(window.location.search);
      }
    }

    /**
     * Another product in the same visit: the recommendation restore() has just computed from the
     * stored measurements is preselected if this product has that combination and it is
     * available. Nothing else is chosen in its place, and a variant given in the URL stays.
     */
    preselect() {
      const rec = this.recommendation;
      if (!rec || !rec.size || !rec.length) return;
      if (this.hasVariantInUrl()) return;
      if (!this.pickerInput(this.data.sizeOption, rec.size, false)) return;
      if (this.data.lengthOption && !this.pickerInput(this.data.lengthOption, rec.length, false)) return;
      if (!this.isAvailable(rec.size, rec.length)) return;

      const sizeChecked = this.pickerInput(this.data.sizeOption, null, true);
      const lengthChecked = this.pickerInput(this.data.lengthOption, null, true);
      const alreadySet = sizeChecked && sizeChecked.value === rec.size && (!this.data.lengthOption || (lengthChecked && lengthChecked.value === rec.length));
      if (alreadySet) return;

      this.select(this.data.sizeOption, rec.size);
      if (this.data.lengthOption) this.select(this.data.lengthOption, rec.length);
    }
  }

  if (!customElements.get('size-chart-modal')) customElements.define('size-chart-modal', SizeChartModal);

  // exposed for tests
  window.stchdSizeChart = { recommend: recommend, tolerances: tolerances, parseMeasure: parseMeasure, BOUNDS: BOUNDS };
})();
